import React from 'react';
import { ChevronRight, Clock, X, Sparkles, AlertTriangle, ArrowRight } from 'lucide-react';
import { useDemo } from '../context/DemoContext';
import { Button } from './ui/button';

/**
 * DemoBanner — a sticky, top-of-page banner that walks the user through
 * the entire MCA platform in 13 steps over a 20-minute window.
 *
 * Visible ONLY when `useDemo().isDemo` is true. Persists across all routes
 * (rendered inside MainLayout, above the page content), survives clicks
 * because step state is backed by the `demo_session` doc on the backend.
 */
const DemoBanner = () => {
  const {
    isDemo, active, step, totalSteps, currentStep, timer, remainingSeconds,
    welcomeOpen, setWelcomeOpen,
    expiredOpen, finish, advance,
  } = useDemo();

  const [confirmEndOpen, setConfirmEndOpen] = React.useState(false);
  const [endingDemo, setEndingDemo] = React.useState(false);

  const handleEndDemo = async () => {
    setEndingDemo(true);
    try {
      await finish();
    } catch {
      // finish() handles its own redirect; if anything fails, force one.
      window.location.replace('/login');
    }
  };

  if (!isDemo) return null;

  const completed = step > totalSteps;
  const lowTime = remainingSeconds > 0 && remainingSeconds <= 60;

  return (
    <>
      {/* ---------- WELCOME MODAL (first login) ---------- */}
      {welcomeOpen && (
        <div
          data-testid="demo-welcome-modal"
          className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <div className="bg-white rounded-2xl max-w-lg w-full p-8 shadow-2xl animate-in fade-in zoom-in duration-300">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <Sparkles className="text-orange-500" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Welcome to your demo!</h2>
            </div>
            <p className="text-gray-700 mb-3 leading-relaxed">
              You've got <strong>20 minutes</strong> to explore Prosper Deal Track end-to-end.
            </p>
            <p className="text-gray-700 mb-3 leading-relaxed">
              We'll guide you through a quick <strong>{totalSteps}-step walkthrough</strong> first
              (it's required so you see everything), then the rest of your time is free play.
            </p>
            <p className="text-gray-700 mb-6 leading-relaxed">
              When the timer hits zero, your account resets so the next person gets a clean slate. No pressure!
            </p>
            <Button
              data-testid="demo-welcome-start-btn"
              onClick={() => setWelcomeOpen(false)}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 text-base"
            >
              Let's go! 🚀
            </Button>
          </div>
        </div>
      )}

      {/* ---------- EXPIRED MODAL ---------- */}
      {expiredOpen && (
        <div
          data-testid="demo-expired-modal"
          className="fixed inset-0 z-[300] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
              <Clock className="text-red-500" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Demo session ended</h2>
            <p className="text-gray-600 mb-6">
              Thanks for trying Prosper Deal Track! Your data is being wiped so the next demo can begin.
              You'll be redirected in a moment.
            </p>
          </div>
        </div>
      )}

      {/* ---------- STICKY BANNER ---------- */}
      {active && currentStep && (
        <div
          data-testid="demo-banner"
          className={`relative w-full ${lowTime ? 'bg-gradient-to-r from-red-600 to-orange-600' : completed ? 'bg-gradient-to-r from-emerald-600 to-teal-600' : 'bg-gradient-to-r from-orange-500 via-orange-600 to-amber-600'} text-white shadow-lg`}
        >
          <div className="px-6 py-3 flex items-center gap-4">
            {/* Step badge */}
            <div className="flex-shrink-0 hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-white/20 backdrop-blur font-bold text-lg" data-testid="demo-banner-step">
              {completed ? '✓' : step}
            </div>

            {/* Title + body */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm sm:text-base truncate" data-testid="demo-banner-title">
                  {currentStep.title}
                </span>
                {!completed && currentStep.cta && (
                  <span className="hidden md:inline-flex items-center gap-1 text-xs bg-white/25 px-2 py-0.5 rounded-full">
                    <ChevronRight size={12} />
                    Look for: <strong>{currentStep.cta}</strong>
                  </span>
                )}
              </div>
              <p className="text-xs sm:text-sm text-white/90 truncate" data-testid="demo-banner-body">
                {currentStep.body}
              </p>
            </div>

            {/* Countdown */}
            <div
              data-testid="demo-banner-timer"
              className={`flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg backdrop-blur ${lowTime ? 'bg-white/30 animate-pulse' : 'bg-black/20'}`}
            >
              <Clock size={16} />
              <span className="font-mono font-bold text-sm sm:text-base tabular-nums">{timer}</span>
            </div>

            {/* Got it / Next button — only on info-only steps */}
            {currentStep?.manual_advance && (
              <button
                data-testid="demo-banner-next-btn"
                onClick={() => advance(step + 1)}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-orange-600 font-bold text-xs sm:text-sm hover:bg-orange-50 transition-colors shadow"
              >
                Got it, next
                <ArrowRight size={14} />
              </button>
            )}

            {/* End demo & reset for next user */}
            <button
              data-testid="demo-banner-end-btn"
              onClick={() => setConfirmEndOpen(true)}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold text-xs sm:text-sm transition-colors shadow"
              title="End demo and reset site for the next user"
            >
              <X size={14} />
              <span className="hidden sm:inline">End demo</span>
            </button>
          </div>

          {/* Progress bar */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
            <div
              className="h-full bg-white/80 transition-all duration-500"
              style={{ width: `${Math.min(100, (Math.min(step, totalSteps) / totalSteps) * 100)}%` }}
            />
          </div>

          {/* Low-time warning bar */}
          {lowTime && (
            <div
              data-testid="demo-banner-low-time"
              className="bg-red-700 text-white text-xs text-center py-1 flex items-center justify-center gap-2"
            >
              <AlertTriangle size={12} />
              Less than 1 minute left — your demo will reset soon!
            </div>
          )}
        </div>
      )}

      {/* ---------- END DEMO CONFIRM MODAL ---------- */}
      {confirmEndOpen && (
        <div
          data-testid="demo-end-confirm-modal"
          className="fixed inset-0 z-[250] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-7 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <X className="text-red-600" size={24} />
              </div>
              <h2 className="text-xl font-bold text-gray-900">End the demo?</h2>
            </div>
            <p className="text-gray-700 mb-6 leading-relaxed">
              This will <strong>wipe all the data you created</strong>, log you out, and reset the site for the next user.
            </p>
            <div className="flex gap-3">
              <Button
                data-testid="demo-end-confirm-cancel"
                onClick={() => setConfirmEndOpen(false)}
                disabled={endingDemo}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold"
              >
                Cancel
              </Button>
              <Button
                data-testid="demo-end-confirm-yes"
                onClick={handleEndDemo}
                disabled={endingDemo}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold"
              >
                {endingDemo ? 'Ending…' : 'Yes, end demo'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DemoBanner;
